import React, {useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {  CELL_RENDERERS, Table, TABLE_LAYOUT_OPTIONS, Dialog } from 'cdk-radial';
import './ActiveSubscriptionsTable.scss';
import CustomContextMenu from './CustomContextMenu';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import config from '../../../../utils/config';
import { getLatestDate, dateComparator, onGridReady } from '../common';
import ShowLocations from '../ShowLocations';
import Unsubscribe from '../../menuOptions/unsubscribe/Unsubscribe';
import { setScreen } from '../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { getSolutionSubscriptionInfo, getEntityById } from '../../../../redux/connectedComponent/subscriptionManagement/subscriptionManagement.slice';
import { screens } from '../../../config/uiFactory.config';
import Notification from '../../../notification/Notification';
import AuthComponent from '../../../common/AuthComponent';
import { modifyStr } from '../../../../utils/utils';
import { AppLink } from '../utils/AppLink';
import { mapLocationsToTable, csvData } from '../utils/utils';
import { sendAmplitudeData } from '../../../../utils/amplitude';
import { AMPLITUDE_EVENTS, AMPLITUDE_PROPERTIES } from '../../../../constants/amplitudeConstants';

const UNSUBSCRIPTION_FAILED = 'App unsubscription failed';
const DEACTIVATION_FAILED = 'App deactivation failed';
const UNSUBSCRIPTION_SUCCEEDED = 'You have successfully unsubscribed from';
const DEACTIVATION_SUCCEEDED =  'You have successfully deactivated from'
const POSITIVE = 'positive';
const NEGATIVE = 'negative';
const REPORT_HEADER = ['App Name', 'Method', 'Publisher Name', 'Location', 'Dealership Address', 'CMF', 'Organization Id', 'Subscription/Activation Date', 'Status'];
const EMPTY_ROW = [,,,,,,,,];
const ACTIVE = 'Active';
const COMMA = ',';
const SUBSCRIPTION = 'Subscription';
const ACTIVATION = 'Activation';

const ActiveSubscriptionsTable = ({
  solutionData = {overview: {}},
  activateUnsubscription,
  subscriptionData,
  isBulkSubscribed,
  selectedPlanId,
  activeTableLayout,
  checkActiveTablePagination,
  searchInput,
  createCSV
}) => {

  const dispatch = useDispatch();

  const renderCustomContextMenu = gridParams => {
    return <CustomContextMenu gridParams={gridParams}  setIsAppNotListed={setIsAppNotListed}/>;
  }

  const renderShowLocations = gridParams => {
    return <ShowLocations gridParams={gridParams} />
  }

  const state = useSelector(state => state);
  const token = state.loginDetailsSlice && state.loginDetailsSlice.authenticationToken;
  const activeSubscriptions = state.subscriptionManagementSlice.activeSubscriptions;
  const activeBulkSubscriptions = state.subscriptionManagementSlice.activeBulkSubscriptions;
  const solutionSubscriptionInfo = state.subscriptionManagementSlice.solutionSubscriptionInfo;
  const fetchingSubscriptions = state.subscriptionManagementSlice.fetchingSubscriptions;
  const allLocations = state.loginDetailsSlice && state.loginDetailsSlice.allLocations;
  const entityIds = uniq(allLocations.map(item => item.entityId));
  const appProvider = state.subscriptionManagementSlice.appProvider;

  const [activeSubscriptionsData, setActiveSubscriptionsData] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationContent, setNotificationContent] = useState(false);
  const [notificationType, setNotificationType] = useState(false);
  const [unsubscribeSuccessful, setUnsubscribeSuccessful] = useState(false);
  const [clickedApp, setClickedApp] = useState('');
  const [clickedAppId, setClickedAppId] = useState('');
  const [isAppNotListed, setIsAppNotListed] = useState(false);


  const getStoreId = (item) => {
    const matchingLocation = allLocations.find(
      (location) => location.entityId === item.entityId
    );
    if (matchingLocation && matchingLocation.storeId) {
      return matchingLocation.storeId;
    }
    return null;
  };

  const getSubscriptionData = (subscription) => {
    const subscriptionData = [];
    subscription && subscription.map(item => {
      subscriptionData.push({
        entityId:item.entityId,
        entityIdList:item.entityIdList,
        entitycmfNumber:item.entitycmfNumber,
        storeId: getStoreId(item),
        subscriptionId:item.subscriptionId,
        subscribedOn: item.lastUpdatedOn || item.lastUpdated,
        storeId: item.orgUnitId,
      })
    });
    return subscriptionData;
  }

  const goToMarketplace = (orgId, appName, appId) => {
    if (appName) {
      dispatch(getEntityById({ orgId, token }));
      setClickedApp(appName);
      setClickedAppId(appId);
    }
    else{
      setIsAppNotListed(true);
    }
  }

  const column = [
    {
      cellRendererFramework: AppLink,
      field: 'appName',
      headerName: 'App Name',
      width: 400,
      minWidth: 400,
      cellRendererParams: {
        goToMarketplace
      }
    },
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'method',
      headerName: 'Method',
      width: 135,
      minWidth: 135
    },
    {
      cellRendererFramework: renderShowLocations,
      field: 'location',
      headerName: 'Location(s)',
      width: 110,
      minWidth: 110
    },
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'publisher',
      headerName: 'Publisher',
      width: 290,
      minWidth: 290
    },
    {
      cellRendererFramework: renderCustomContextMenu,
      field: 'subscribedOn',
      headerName: 'Subscribed/Activated On',
      width: 270,
      minWidth: 270,
      sortable: true,
      unSortIcon: true,
      comparator: dateComparator
    }
  ];

  useEffect(()=>{
    let activeData = [];
    let item = null;
    if(activeSubscriptions){
      map(activeSubscriptions, (subscription, index) => {
        if(solutionSubscriptionInfo[index]){
          item = {
            appName: solutionSubscriptionInfo[index]?.overview?.name || solutionSubscriptionInfo[index].displayName,
            location: subscription.length,
            publisher: solutionSubscriptionInfo[index]?.overview?.publisherName || solutionSubscriptionInfo[index].orgName,
            subscribedOn: getLatestDate(subscription),
            solutionData: solutionSubscriptionInfo[index],
            selectedPlanId: subscription && subscription[0] && subscription[0].selectedPlanId,
            subscriptionData: getSubscriptionData(subscription),
            method: solutionSubscriptionInfo[index].activateOnly!==false ? ACTIVATION : SUBSCRIPTION,
            status: 'active',
            isBulkSubscribed: false,
            rowHeight: 52,
            solutionLogo: solutionSubscriptionInfo[index]?.overview?.solutionLogo,
            orgId: solutionSubscriptionInfo[index]?.orgId,
            appId: solutionSubscriptionInfo[index]?.id,
            privacyProtection: solutionSubscriptionInfo[index]?.privacyProtection
          };
          activeData.push(item);
        }
      });
    }

    if(activeBulkSubscriptions){
      map(activeBulkSubscriptions, (subscription, index) => {
        if(solutionSubscriptionInfo[index]){
          item = {
            appName: solutionSubscriptionInfo[index]?.overview?.name || solutionSubscriptionInfo[index].displayName,
            location: subscription.length,
            publisher: solutionSubscriptionInfo[index]?.overview?.publisherName || solutionSubscriptionInfo[index].orgName,
            subscribedOn: getLatestDate(subscription),
            solutionData: solutionSubscriptionInfo[index],
            selectedPlanId: subscription && subscription[0] && subscription[0].selectedPlanId,
            subscriptionData: getSubscriptionData(subscription),
            method: 'Fortellis Admin',
            status: 'active',
            isBulkSubscribed: true,
            rowHeight: 52,
            solutionLogo: solutionSubscriptionInfo[index]?.overview?.solutionLogo,
            orgId: solutionSubscriptionInfo[index]?.orgId,
            appId: solutionSubscriptionInfo[index]?.id,
            privacyProtection: solutionSubscriptionInfo[index]?.privacyProtection
          };
          activeData.push(item);
        }
      })
    }
    setActiveSubscriptionsData(activeData);

    let subscriptionsForReport = [REPORT_HEADER, EMPTY_ROW];
    activeData?.map(subscription => {
      subscription?.subscriptionData?.map(data => {
          let locationData = mapLocationsToTable([data], allLocations)?.[0];
          let appName = subscription?.appName?.replace(COMMA, '');
          let subscribedOn = locationData?.subscribedOn;
          if(appName) {
            subscriptionsForReport.push(csvData({subscription, locationData, data, subscribedOn, status: ACTIVE}));
          }
      })
    })

    createCSV(subscriptionsForReport);
  },[activeSubscriptions, solutionSubscriptionInfo]);

  useEffect(() => {
    if(appProvider && clickedApp && clickedAppId) {
      let provider = modifyStr(appProvider.replace(/[` ~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/g, '-').toLowerCase());
      let appName = modifyStr(clickedApp.replace(/[` ~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/g, '-').toLowerCase());
      window.open(`${config.url.marketplace}solutions/${provider}/${appName}?id=${clickedAppId}`);
      setClickedApp('');
      setClickedAppId('');
    }
  }, [appProvider]);

  useEffect(()=>{
    if(fetchingSubscriptions){
      setActiveSubscriptionsData([]);
    }
  },[fetchingSubscriptions]);

  const onUnsubscribeOverlayClose = () => {
    sendAmplitudeData(AMPLITUDE_EVENTS.CLOSE_UNSUBSCRIBE,{
      [AMPLITUDE_PROPERTIES.APP_NAME] : solutionData?.overview?.name || solutionData.displayName,
      [AMPLITUDE_PROPERTIES.APP_ID]: solutionData?.id
    })
    dispatch(setScreen({
        screen: screens.SUBSCRIPTION_MANAGEMENT,
        additionalData: {
          activateUnsubscription: false
      }
    }));
  }

  const onUnsubscribeOverlayAccept = (appName, unsubscribeFailed,isSubscription) => {
    sendAmplitudeData(AMPLITUDE_EVENTS.UNSUBSCRIBE,{
      [AMPLITUDE_PROPERTIES.APP_NAME] : solutionData?.overview?.name || solutionData.displayName,
      [AMPLITUDE_PROPERTIES.APP_ID]: solutionData?.id
    })
    onUnsubscribeOverlayClose();
    if(unsubscribeFailed) {
      let notificationText = isSubscription ? UNSUBSCRIPTION_FAILED : DEACTIVATION_FAILED
      setNotificationContent(notificationText);
      setNotificationType(NEGATIVE);
      setUnsubscribeSuccessful(false);
    }
    else {
      let notificationText = isSubscription ? UNSUBSCRIPTION_SUCCEEDED : DEACTIVATION_SUCCEEDED
      setNotificationContent(`${notificationText} ${appName}`);
      setNotificationType(POSITIVE);
      setUnsubscribeSuccessful(true);
    }
    setShowNotification(true);
  }

  const onDismiss = () => {
    if(unsubscribeSuccessful) {
      dispatch(getSolutionSubscriptionInfo({entityIds,token}))
    }
  }

  const getRowHeight = params => params.data.rowHeight;

  return (
    <div>
      <div
        className={
          activeTableLayout === TABLE_LAYOUT_OPTIONS.NORMAL
            ? 'active_table'
            : 'active_table_no-pagination'
        }
      >
        <Table
          columns={column}
          data={activeSubscriptionsData?.filter(data => searchInput ? data?.appName?.toLowerCase()?.includes(searchInput.toLowerCase()) || data?.publisher?.toLowerCase()?.includes(searchInput?.toLowerCase())  : true)}
          getRowHeight={getRowHeight}
          hasPagination={checkActiveTablePagination}
          isSearchable={false}
          isDownloadable={false}
          isPrintable={false}
          resizableHeaders={false}
          showNoRowsOverlay={false}
          showFooter={false}
          hideTableTitle={true}
          hideTableHeader={true}
          tableLayout={activeTableLayout}
          id="table-with-auto-height-1"
          noRowsFoundMessage='Looks like you could use a few App subscriptions to manage'
          onGridReady={onGridReady}
          sortingOrder={['asc', null, 'desc']}
          paginationClassName={'active_table-pagination'}
          />
      </div>
      {activateUnsubscription && (
        <Unsubscribe
          active={true}
          title={`${solutionData.activateOnly === false ? 'Unsubscribe' :'Deactivate' } from App`}
          appName={solutionData?.overview?.name || solutionData.displayName}
          onUnsubscribeOverlayClose={onUnsubscribeOverlayClose}
          onUnsubscribeOverlayAccept={onUnsubscribeOverlayAccept}
          solutionData={solutionData}
          selectedPlanId={selectedPlanId}
          useDispatch={useDispatch}
          useSelector={useSelector}
          activeSubscriptionsData={activeSubscriptionsData}
          isBulkSubscribed={isBulkSubscribed}
          subscriptionData={subscriptionData}
        />
      )}
      {showNotification && (
        <Notification
          content={notificationContent}
          variant={notificationType}
          actions={[{ text: 'Dismiss' }]}
          onDismiss={() => {
            setShowNotification(false);
            onDismiss();
          }}
          customStyle={{ marginTop: '70px' }}
        />
      )}

<Dialog

        isOpen={isAppNotListed}
        dataTestId="restricted-subscription-management-dialog"
        hideCloseButton={false}
        onClose={() => setIsAppNotListed(false)}
        id="overlay-id"
        closeLabel="Close"
        title = 'App has been removed from Marketplace'
       >
        This app is no longer available in Marketplace. Contact the Publisher for more details.
       </Dialog>
    </div>
  );
}

export default AuthComponent(ActiveSubscriptionsTable);
